import { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFormById, clearCurrentForm } from '../reducers/formsSlice';
import { setFormDirty } from '../reducers/uiSlice';
import { useGeoLocation } from './useGeoLocation';
import { useFormValidation } from './useFormValidation';
import { useFormSubmission } from './useFormSubmission';
import { toastService } from '../services/toastService';
import { AuthContext } from '../context/AuthContext';
import { processFormCalculations } from '../utils/calculationEngine';
import { submissionService } from '../services/submissionService';
import { entityService } from '../services/entityService';

// Build bidirectional maps: field.id ↔ field.name
const buildFieldMaps = sections => {
  const idToName = {};
  const nameToId = {};
  const traverse = secs => {
    secs?.forEach(s => {
      (s.fields || []).forEach(f => {
        if (f.id && f.name) {
          idToName[f.id] = f.name;
          nameToId[f.name] = f.id;
        }
      });
      if (s.sections) traverse(s.sections);
    });
  };
  traverse(sections);
  return { idToName, nameToId };
};

// Convert id-keyed formValues → name-keyed values for the calculation engine
const toNameValues = formValues => formValues;

const HIDDEN_FIELD_TYPES = ['calculate', 'enumerator', 'hidden', 'deviceid', 'username', 'start', 'end', 'subscriberid', 'simserial', 'phonenumber', 'caseid', 'audit'];

// Field types that cannot be edited (binary/media uploads)
const NON_EDITABLE_FIELD_TYPES = new Set([
  'file', 'multi-file', 'file_multiple', 'image', 'video',
  'audio', 'document', 'signature',
  'geopoint', 'geotrace', 'geoshape'
]);

const getTodayValue = (type) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  
  if (type === 'date') {
    return `${year}-${month}-${day}`;
  }
  if (type === 'datetime' || type === 'datetime-local') {
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
  return '';
};

export const useFormEntry = (formIdParam, submissionIdParam) => {
  const { formId: formIdUrl, submissionId: submissionIdUrl } = useParams();
  const formId = formIdParam || formIdUrl;
  const submissionId = submissionIdParam || submissionIdUrl;
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();
  const { user } = useContext(AuthContext);
  const { currentForm: form, status } = useSelector(state => state.forms);
  const [formValues, setFormValues] = useState({});
  const [activeRepeats, setActiveRepeats] = useState({});
  const [retryCount, setRetryCount] = useState(0);
  const [fileUploadStatus, setFileUploadStatus] = useState({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  const [isPreloading, setIsPreloading] = useState(!!submissionId);
  const [originalValues, setOriginalValues] = useState({});
  const [originalRepeats, setOriginalRepeats] = useState({});
  const [nonEditableFieldNames, setNonEditableFieldNames] = useState(new Set());
  
  const [entitySelected, setEntitySelected] = useState(null);
  const [readOnlyFields, setReadOnlyFields] = useState({});
  const [isConfirmed, setIsConfirmed] = useState(searchParams.get('confirmed') === 'true');

  const entityIdFromUrl = searchParams.get('entity_id');

  const { locationData, isLocationLoading, refreshLocation, permissionDenied, positionUnavailable } = useGeoLocation();

  useEffect(() => {
    if (formId) {
      dispatch(fetchFormById(formId));
    }
    return () => {
      dispatch(clearCurrentForm());
    };
  }, [formId, dispatch]);

  // Handle Editing Initialization
  useEffect(() => {
    if (submissionId && form?.sections) {
      setIsPreloading(true);
      submissionService
        .getSubmissionById(submissionId)
        .then(res => {
          const submission = res.data.data;
          if (!submission) return;

          const initialValues = {};
          const initialRepeats = {};

          const answers = submission.answers || [];
          const isFlatAnswers = !Array.isArray(answers);

          const answersMap = isFlatAnswers
            ? answers
            : answers.reduce((acc, a) => {
                acc[a.form_field_id] = a;
                return acc;
              }, {});

          const traverse = (sections, pathPrefix = '', repeatData = null) => {
            sections?.forEach(sec => {
              const isRepeat = !!sec.is_repeat;
              const sectionKey = pathPrefix ? `${pathPrefix}${sec.name}` : sec.name;

              if (isRepeat) {
                let count = 0;
                if (repeatData?.[sec.id]) {
                  count = repeatData[sec.id].items?.length || 0;
                } else if (isFlatAnswers) {
                  const pattern = new RegExp(`^${sectionKey}_(\\d+)_`);
                  const indices = Object.keys(answers)
                    .map(k => k.match(pattern)?.[1])
                    .filter(Boolean)
                    .map(Number);
                  count = indices.length > 0 ? Math.max(...indices) + 1 : parseInt(sec.repeat_count) || 0;
                } else {
                  count = parseInt(sec.repeat_count) || 0;
                }

                if (count > 0) {
                  initialRepeats[sectionKey] = count;
                  for (let i = 0; i < count; i++) {
                    const itemPath = `${pathPrefix}${sec.name}_${i}_`;
                    const itemRepeatData = repeatData?.[sec.id]?.items?.[i] || null;

                    sec.fields?.forEach(f => {
                      const k = itemPath + f.name;
                      let val = undefined;

                      if (isFlatAnswers) {
                        val = answers[k];
                      } else if (itemRepeatData) {
                        const ans = itemRepeatData.answers?.find(a => a.form_field_id === f.id);
                        if (ans) {
                          const valJson = ans.value_JSON || ans.value_json;
                          const rawValue = ans.value;

                          if (valJson) {
                            try {
                              val = JSON.parse(valJson);
                            } catch {
                              val = valJson;
                            }
                          } else if (typeof rawValue === 'string' && (rawValue.startsWith('[') || rawValue.startsWith('{'))) {
                            try {
                              val = JSON.parse(rawValue);
                            } catch {
                              val = rawValue;
                            }
                          } else {
                            val = rawValue;
                          }
                        }
                      }

                      if (val !== undefined) initialValues[k] = val;
                    });
                    if (sec.sections) traverse(sec.sections, itemPath, itemRepeatData);
                  }
                }
              } else {
                sec.fields?.forEach(f => {
                  const fieldName = pathPrefix ? pathPrefix + f.name : f.name;
                  let val = undefined;

                  if (isFlatAnswers) {
                    val = answers[fieldName];
                  } else {
                    const ans = answersMap[f.id];
                    if (ans) {
                      const valJson = ans.value_JSON || ans.value_json;
                      const rawValue = ans.value;

                      if (valJson) {
                        try {
                          val = JSON.parse(valJson);
                        } catch {
                          val = valJson;
                        }
                      } else if (typeof rawValue === 'string' && (rawValue.startsWith('[') || rawValue.startsWith('{'))) {
                        try {
                          val = JSON.parse(rawValue);
                        } catch {
                          val = rawValue;
                        }
                      } else {
                        val = rawValue;
                      }
                    }
                  }

                  if (val !== undefined) initialValues[fieldName] = val;
                });
                if (sec.sections) traverse(sec.sections, pathPrefix, repeatData);
              }
            });
          };

          const rootRepeatData = (submission.answers_repeats || []).reduce((acc, r) => {
            acc[r.form_section_id] = r;
            return acc;
          }, {});

          traverse(form.sections, '', rootRepeatData);

          // Identify non-editable fields (file/image/audio/video/doc/signature/geo)
          const nonEditableNames = new Set();
          const nonEditableReadOnly = {};
          const collectNonEditable = (sections, pathPrefix = '') => {
            sections?.forEach(sec => {
              const isRepeat = !!sec.is_repeat;
              if (isRepeat) {
                const key = pathPrefix ? `${pathPrefix}${sec.name}` : sec.name;
                const count = initialRepeats[key] || 0;
                for (let i = 0; i < count; i++) {
                  const itemPath = `${pathPrefix}${sec.name}_${i}_`;
                  sec.fields?.forEach(f => {
                    if (NON_EDITABLE_FIELD_TYPES.has(f.type?.toLowerCase())) {
                      const fieldName = itemPath + f.name;
                      nonEditableNames.add(fieldName);
                      nonEditableReadOnly[fieldName] = true;
                    }
                  });
                  if (sec.sections) collectNonEditable(sec.sections, itemPath);
                }
              } else {
                sec.fields?.forEach(f => {
                  if (NON_EDITABLE_FIELD_TYPES.has(f.type?.toLowerCase())) {
                    const fieldName = pathPrefix ? pathPrefix + f.name : f.name;
                    nonEditableNames.add(fieldName);
                    nonEditableReadOnly[fieldName] = true;
                  }
                });
                if (sec.sections) collectNonEditable(sec.sections, pathPrefix);
              }
            });
          };
          collectNonEditable(form.sections);
          setNonEditableFieldNames(nonEditableNames);
          setReadOnlyFields(prev => ({ ...prev, ...nonEditableReadOnly }));

          setFormValues(prev => ({ ...prev, ...initialValues }));
          setActiveRepeats(prev => ({ ...prev, ...initialRepeats }));
          setOriginalValues(initialValues);
          setOriginalRepeats(initialRepeats);
        })
        .finally(() => setIsPreloading(false));
    }
  }, [submissionId, form, user]);

  // Fetch entity if entity_id is in URL (handles page refresh)
  useEffect(() => {
    if (entityIdFromUrl && !entitySelected) {
      const fetchEntity = async () => {
        try {
          const response = await entityService.getById(entityIdFromUrl);
          const entity = response?.data || response;
          setEntitySelected(entity);
        } catch (error) {
          console.error('Failed to fetch entity from URL', error);
        }
      };
      fetchEntity();
    }
  }, [entityIdFromUrl, entitySelected]);

  // Mode-aware form pre-fill whenever an entity is selected
  const prefillDoneRef = useRef(false);

  // Reset the prefill guard whenever the selected entity changes
  useEffect(() => {
    prefillDoneRef.current = false;
  }, [entitySelected?.id]);

  useEffect(() => {
    if (!entitySelected || !form?.sections || prefillDoneRef.current) return;
    prefillDoneRef.current = true;

    const mode = form.entity_action_default;

    if (mode === 'update') {
      // Pre-fill from the most recent submission for this form
      const submissions = entitySelected.submissions || [];
      const relevant = submissions
        .filter(s => s.form_id === form.id)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const submission = relevant[0] || submissions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

      if (submission?.answers?.length > 0) {
        const submissionValues = {};
        const SKIP_TYPES = new Set(['start','end','enumerator','hidden','deviceid','username','subscriberid','simserial','phonenumber','caseid','audit']);
        submission.answers.forEach(answer => {
          const fieldName = answer.field?.name;
          if (!fieldName) return;
          if (SKIP_TYPES.has(answer.field?.type?.toLowerCase())) return;
          let val = answer.value;
          const raw = answer.value_json;
          if (raw) {
            try { val = JSON.parse(raw); } catch { val = raw; }
          } else if (typeof val === 'string' && (val.startsWith('[') || val.startsWith('{'))) {
            try { val = JSON.parse(val); } catch {}
          }
          submissionValues[fieldName] = val;
        });
        setFormValues(prev => ({ ...prev, ...submissionValues }));
      }
    } else {
      // create mode: pre-fill form fields from entity attributes (read-only)
      const nextReadOnly = {};
      setFormValues(prev => {
        const next = { ...prev };
        const traverse = (sections, pathPrefix = '') => {
          sections.forEach(sec => {
            sec.fields?.forEach(field => {
              const fieldName = pathPrefix ? pathPrefix + field.name : field.name;
              const val = entitySelected.attributes_json?.[field.key || field.name];
              if (val !== undefined) {
                next[fieldName] = val;
                nextReadOnly[fieldName] = true;
              }
            });
            if (sec.sections) traverse(sec.sections, pathPrefix);
          });
        };
        traverse(form.sections);
        return next;
      });
      setReadOnlyFields(nextReadOnly);
    }
  }, [entitySelected, form]);

  useMemo(() => buildFieldMaps(form?.sections || []), [form]);

  const nameValues = useMemo(() => toNameValues(formValues), [formValues]);

  const { errors, countryCode } = useFormValidation(formValues, form, nameValues, activeRepeats);
  const { handleSubmit, submissionStatus, submittingAction } = useFormSubmission(
    form, 
    formValues, 
    locationData, 
    countryCode, 
    nameValues, 
    activeRepeats,
    submissionId,
    entitySelected?.id,
    form?.entity_action_default,
    nonEditableFieldNames
  );

  useEffect(() => {
    if (form?.sections && !isPreloading) {
      const initialValues = {};
      const initialRepeats = {};
      const deviceId = localStorage.getItem('deviceId') || `dev_${Math.random().toString(36).substr(2, 9)}`;
      if (!localStorage.getItem('deviceId')) {
        localStorage.setItem('deviceId', deviceId);
      }

      const traverse = (sections, pathPrefix = '') => {
        sections?.forEach(sec => {
          if (sec.is_repeat) {
            const key = pathPrefix ? `${pathPrefix}${sec.name}` : sec.name;
            if (activeRepeats[key] !== undefined) return;

            const count = parseInt(sec.repeat_count) || 0;
            if (count > 0) {
              initialRepeats[key] = count;
              for (let i = 0; i < count; i++) {
                const itemPath = `${pathPrefix}${sec.name}_${i}_`;
                sec.fields?.forEach(f => {
                  const type = f.type?.toLowerCase();
                  const fullFieldName = itemPath + f.name;
                  if (formValues[fullFieldName] === undefined && initialValues[fullFieldName] === undefined) {
                    let val = f.default || '';
                    if (type === 'start') val = new Date().toISOString();
                    if (type === 'username') val = user?.username || 'user';
                    if (type === 'deviceid') val = deviceId;
                    if (type === 'tel' && countryCode) val = countryCode;
                    if (type === 'select_multiple') val = [];
                    if ((type === 'date' || type === 'datetime' || type === 'datetime-local') && f.is_today) {
                      val = getTodayValue(type);
                    }
                    initialValues[fullFieldName] = val;
                  }
                });
                if (sec.sections) traverse(sec.sections, itemPath);
              }
            }
          } else {
            sec.fields?.forEach(f => {
              const type = f.type?.toLowerCase();
              const fieldName = pathPrefix ? pathPrefix + f.name : f.name;
              if (formValues[fieldName] === undefined && initialValues[fieldName] === undefined) {
                let val = f.default || '';
                if (type === 'start') val = new Date().toISOString();
                if (type === 'username') val = user?.username || 'user';
                if (type === 'deviceid') val = deviceId;
                if (type === 'tel' && countryCode) val = countryCode;
                if (type === 'select_multiple') val = [];
                if ((type === 'date' || type === 'datetime' || type === 'datetime-local') && f.is_today) {
                  val = getTodayValue(type);
                }
                initialValues[fieldName] = val;
              }
            });
            if (sec.sections) traverse(sec.sections, pathPrefix);
          }
        });
      };

      traverse(form.sections);
      setFormValues(prev => {
        const merged = { ...prev, ...initialValues };
        const calcUpdates = processFormCalculations({ sections: form.sections }, merged, { ...activeRepeats, ...initialRepeats });
        return { ...merged, ...calcUpdates };
      });
      setActiveRepeats(prev => ({ ...prev, ...initialRepeats }));
    }
  }, [form, countryCode, user, isPreloading]);

  const retryFormLoad = useCallback(() => {
    if (retryCount < 3) {
      setRetryCount(prev => prev + 1);
      dispatch(fetchFormById(formId));
    }
  }, [formId, dispatch, retryCount]);

  const handleFieldChange = (fieldName, value) => {
    dispatch(setFormDirty(true));
    setFormValues(prev => {
      const next = { ...prev, [fieldName]: value };
      if (form?.sections) {
        const calcUpdates = processFormCalculations({ sections: form.sections }, next, activeRepeats);
        return { ...next, ...calcUpdates };
      }
      return next;
    });
  };

  const resetForm = useCallback(() => {
    const initialValues = {};
    const deviceId = localStorage.getItem('deviceId');
    if (form?.questions) {
      form.questions.forEach(q => {
        const type = q.type?.toLowerCase();
        if (type === 'tel' && countryCode) {
          initialValues[q.id] = countryCode;
        } else if (type === 'start') {
          initialValues[q.id] = new Date().toISOString();
        } else if (type === 'username') {
          initialValues[q.id] = user?.username || user?.email || 'guest';
        } else if (type === 'deviceid') {
          initialValues[q.id] = deviceId;
        } else if ((type === 'date' || type === 'datetime' || type === 'datetime-local') && q.is_today) {
          initialValues[q.id] = getTodayValue(type);
        } else if (q.default !== undefined && q.default !== null) {
          initialValues[q.id] = q.default;
        }
      });
    }
    setFormValues(initialValues);
    setFileUploadStatus({});
    setHasAttemptedSubmit(false);
    dispatch(setFormDirty(false));
  }, [form, countryCode, dispatch, user?.username, user?.email]);

  const handleSubmitWrapper = action => {
    const hasCoordinates = locationData && locationData.latitude && locationData.longitude;
    if (!hasCoordinates) {
      if (permissionDenied) {
        toastService.error('Location permission is blocked. Please enable it.');
      } else if (positionUnavailable) {
        toastService.error('Device location is off. Please enable GPS.');
      } else {
        refreshLocation()?.catch(() => {});
        toastService.warning('Location missing. Trying to fetch coordinates...');
      }
      return;
    }

    if (Object.keys(errors).length > 0) {
      setHasAttemptedSubmit(true);
      const firstFieldId = Object.keys(errors)[0];
      const errorObj = errors[firstFieldId];
      const errorMessage = errorObj.message;

      setTimeout(() => {
        const el = document.querySelector(`[data-field-name="${firstFieldId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      let fieldName = 'Field';
      if (form && form.questions) {
        const question = form.questions.find(q => q.id === firstFieldId);
        if (question) {
          fieldName = question.label || question.name || 'Field';
        }
      }
      toastService.error(`${fieldName}: ${errorMessage}`);
      return;
    }

    const hasAnyValue = Object.values(formValues).some(v => v !== null && v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0));

    if (!hasAnyValue && !submissionId) {
      toastService.warning('Cannot submit an empty form.');
      return;
    }

    handleSubmit(action, () => {
      if (action === 'new') {
        resetForm();
      }
    });
  };

  const isDirty = useMemo(() => {
    if (!submissionId) return true;
    const valueKeys = new Set([...Object.keys(formValues), ...Object.keys(originalValues)]);
    for (const key of valueKeys) {
      if (HIDDEN_FIELD_TYPES.some(t => key.endsWith(`_${t}`))) continue;
      // Skip non-editable fields (files, images, audio, etc.) from dirty check
      if (nonEditableFieldNames.has(key)) continue;
      const v1 = formValues[key];
      const v2 = originalValues[key];
      if (v1 === v2) continue;
      if (!v1 && !v2) continue;
      if (Array.isArray(v1) && Array.isArray(v2)) {
        if (v1.length !== v2.length) return true;
        if (JSON.stringify(v1) !== JSON.stringify(v2)) return true;
        continue;
      }
      return true;
    }
    const repeatKeys = new Set([...Object.keys(activeRepeats), ...Object.keys(originalRepeats)]);
    for (const key of repeatKeys) {
      if (activeRepeats[key] !== originalRepeats[key]) return true;
    }
    return false;
  }, [formValues, originalValues, activeRepeats, originalRepeats, submissionId, nonEditableFieldNames]);

  const isSubmitDisabled = useMemo(() => {
    if (submissionStatus === 'submitting') return true;
    if (submissionId && !isDirty) return true;
    return false;
  }, [submissionStatus, isDirty, submissionId]);

  return {
    form,
    status: isPreloading ? 'loading' : status,
    formValues,
    setFormValues,
    nameValues,
    activeRepeats,
    errors,
    hasAttemptedSubmit,
    submissionStatus,
    submittingAction,
    isLocationLoading,
    locationData,
    refreshLocation,
    permissionDenied,
    positionUnavailable,
    fileUploadStatus,
    setFileUploadStatus,
    handleFieldChange,
    handleSubmit: handleSubmitWrapper,
    isSubmitDisabled,
    isDirty,
    retryFormLoad,
    entitySelected,
    setEntitySelected,
    setSearchParams,
    readOnlyFields,
    setReadOnlyFields,
    isConfirmed,
    setIsConfirmed
  };
};
